import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, SectionCard, Empty } from "@/components/shared";
import { fmtDateTime } from "@/lib/format";
import { Mail, MessageSquare, Send, Plus, X, Phone } from "lucide-react";
import { toast } from "sonner";

const CH_ICON = { Email: Mail, SMS: Phone, WhatsApp: MessageSquare };

export default function Comms() {
  const [tab, setTab] = useState("Templates");
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [open, setOpen] = useState(false);
  const [sendFor, setSendFor] = useState(null);

  const load = () => {
    api.get("/comms/templates").then((r) => setTemplates(r.data));
    api.get("/comms/logs").then((r) => setLogs(r.data));
  };
  useEffect(() => { load(); }, []);

  return (
    <div>
      <PageHeader title="Communication Centre" subtitle="Templates, triggers and logs via provider abstraction — manual fallback (no live credentials)." testid="comms-page"
        actions={<button data-testid="new-template-btn" onClick={() => setOpen(true)} className="flex items-center gap-2 text-white font-semibold px-4 py-2 rounded-lg copper-glow" style={{ background: "var(--copper)" }}><Plus size={16} /> New Template</button>} />

      <div className="flex gap-1 border-b border-slate-200 mb-5">
        {["Templates", "Send Log"].map((t) => <button key={t} onClick={() => setTab(t)} data-testid={`comms-tab-${t.replace(/ /g, "-").toLowerCase()}`} className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab === t ? "border-[var(--copper)] text-slate-900" : "border-transparent text-slate-500"}`}>{t}</button>)}
      </div>

      {tab === "Templates" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((t) => {
            const Icon = CH_ICON[t.channel] || Mail;
            return (
              <div key={t.id} className="card-surface p-5 flex flex-col">
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600"><Icon size={12} /> {t.channel}</span>
                </div>
                <h4 className="font-semibold text-slate-800 mt-3">{t.name}</h4>
                {t.subject && <p className="text-xs text-slate-500 mt-1">{t.subject}</p>}
                <p className="text-xs text-slate-500 mt-2 flex-1 line-clamp-3">{t.body}</p>
                <button onClick={() => setSendFor(t)} data-testid={`send-${t.id}`} className="mt-4 flex items-center justify-center gap-1.5 text-sm text-white font-semibold px-3 py-1.5 rounded-lg" style={{ background: "var(--navy-800)" }}><Send size={14} /> Send (test)</button>
              </div>
            );
          })}
          {templates.length === 0 && <Empty />}
        </div>
      )}

      {tab === "Send Log" && (
        <SectionCard className="!p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100"><th className="px-5 py-3">Template</th><th className="px-5 py-3">Channel</th><th className="px-5 py-3">To</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Sent</th></tr></thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className="border-b border-slate-50">
                    <td className="px-5 py-3">{l.template_name}</td><td className="px-5 py-3 text-slate-600">{l.channel}</td>
                    <td className="px-5 py-3 font-mono text-xs">{l.to}</td>
                    <td className="px-5 py-3"><span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">{l.status}</span></td>
                    <td className="px-5 py-3 text-xs text-slate-400">{fmtDateTime(l.timestamp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {logs.length === 0 && <Empty />}
          </div>
        </SectionCard>
      )}

      {open && <TemplateDialog onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); toast.success("Template created"); }} />}
      {sendFor && <SendDialog tpl={sendFor} onClose={() => setSendFor(null)} onDone={() => { setSendFor(null); load(); setTab("Send Log"); toast.success("Queued via provider abstraction (manual fallback)"); }} />}
    </div>
  );
}

function TemplateDialog({ onClose, onDone }) {
  const [f, setF] = useState({ name: "", channel: "Email", subject: "", body: "" });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async () => {
    if (!f.name || !f.body) { toast.error("Name and body required"); return; }
    try { await api.post("/comms/templates", f); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="template-dialog">
        <div className="flex justify-between items-center mb-4"><h3 className="font-bold text-slate-900">New Template</h3><button onClick={onClose} className="text-slate-400"><X size={18} /></button></div>
        <div className="space-y-3">
          <input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Template name" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="template-name" />
          <select value={f.channel} onChange={(e) => set("channel", e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">{["Email", "SMS", "WhatsApp"].map((c) => <option key={c}>{c}</option>)}</select>
          <input value={f.subject} onChange={(e) => set("subject", e.target.value)} placeholder="Subject (email)" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          <textarea value={f.body} onChange={(e) => set("body", e.target.value)} placeholder="Message body — use {{name}}, {{points}} tokens" rows={4} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="template-body" />
          <button onClick={submit} data-testid="template-submit" className="w-full text-white font-semibold py-2.5 rounded-lg copper-glow" style={{ background: "var(--copper)" }}>Save Template</button>
        </div>
      </div>
    </div>
  );
}

function SendDialog({ tpl, onClose, onDone }) {
  const [to, setTo] = useState("");
  const submit = async () => {
    if (!to) { toast.error("Recipient required"); return; }
    try { await api.post("/comms/send", { template_id: tpl.id, to }); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-bold text-slate-900 mb-1">Send: {tpl.name}</h3>
        <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2 my-3">No live {tpl.channel} provider — this logs a "queued" entry via the provider abstraction.</p>
        <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="Recipient (phone/email)" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-4" data-testid="send-to" />
        <button onClick={submit} data-testid="send-confirm" className="w-full text-white font-semibold py-2.5 rounded-lg" style={{ background: "var(--copper)" }}>Send</button>
      </div>
    </div>
  );
}
